import BasicList from 'core/modules/components/lists/components/BasicList/BasicList';
import AssessmentItem from 'core/modules/components/lists/assessments/AssessmentItem/AssessmentItem';
import React from 'react';

/**
 * AssessmentsList - Lista de evaluaciones/tareas del alumno. Envuelve el genérico v2
 * `BasicList` (semántica `<ul>/<li>` accesible) y renderiza cada assessment dentro de un
 * `<button>` accesible (focusable + activable por teclado), en vez del antiguo `<div onClick>`
 * sin `role`/`tabIndex`. Mismo envoltorio `<button>` que `TaskList`; el nombre accesible, en
 * cambio, sale del contenido y no de un `aria-label` resumido como el que `TaskList` aún usa.
 *
 * El nombre accesible del botón se calcula desde su contenido (`AssessmentItem`): asignatura,
 * lección, título, fecha límite, estado y nota. No lleva `aria-label`, que reemplazaría ese
 * nombre por un resumen. Que así se respete también el `lang` del contenido en modo exploración
 * es una suposición pendiente de QA con lector: al enfocar el botón, el nombre es texto plano.
 *
 * Las tareas fuera de plazo (`status: 'timed-out'`) no son accionables: se marcan con
 * `aria-disabled` —siguen siendo enfocables y anunciadas— y no propagan el click. El `Tag`
 * «Fuera de tiempo» que pinta `AssessmentItem` es el que explica el porqué.
 */

const TIMED_OUT_STATUS = 'timed-out';

// Aquí solo se leen `lesson_item_guid` (key) y `status`; el resto de props las consume
// `AssessmentItem` (JS sin tipar) vía spread, cubiertas por el index signature `Record<string, unknown>`.
export type Assessment = Readonly<{
  lesson_item_guid?: string;
  status?: string;
}> &
  Readonly<Record<string, unknown>>;

type AssessmentsListProps = Readonly<{
  assessments?: Assessment[];
  onClick?: (assessment: Assessment) => void;
}>;

type AssessmentListItemProps = Readonly<{
  assessment: Assessment;
  onClick: (assessment: Assessment) => void;
}>;

function AssessmentListItem({ assessment, onClick }: AssessmentListItemProps): JSX.Element {
  const isTimedOut = assessment.status === TIMED_OUT_STATUS;

  function handleClick(): void {
    if (isTimedOut) return;
    onClick(assessment);
  }

  return (
    <button
      aria-disabled={isTimedOut}
      className="button-accessibility-only lesson-task-list__task lesson-task-list__task-button"
      onClick={handleClick}
      type="button"
    >
      <AssessmentItem {...assessment} />
    </button>
  );
}

function AssessmentsList({ assessments = [], onClick = () => {} }: AssessmentsListProps): JSX.Element {
  return (
    <div className="assessments-list">
      <BasicList
        items={assessments.map((assessment) => (
          <AssessmentListItem key={assessment.lesson_item_guid} assessment={assessment} onClick={onClick} />
        ))}
      />
    </div>
  );
}

export default AssessmentsList;
